import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PageContainer } from "@/components/ui/PageContainer";
import { SeoHead } from "@/components/seo/SeoHead";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  MessageCircleQuestion,
  Plus,
  Minus,
} from "lucide-react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import quizData from "@/data/copy.quiz_v2_1";
import type { QuizQuestion, QuizSchema } from "@/types/quiz";
import { evaluateCondition, type AnswerMap } from "@/utils/quizRuntime";
import RiskIQGaugeHC from "@/components/ui/RiskIQGaugeHC";
import { getRiskBand } from "@/utils/getRiskBand";
import { computeScores } from "@/utils/score";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Link, useNavigate } from "react-router-dom";
import { DisclaimerMini } from "@/components/disclaimers/DisclaimerMini";
import { DisclaimerFull } from "@/components/disclaimers/DisclaimerFull";
import { Input } from "@/components/ui/input";
import { validateEmailFormat, validateEmailWithSuggestion } from "@/lib/emailValidation";
import { supabase } from "@/integrations/supabase/client";

function Choice({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full px-4 py-3 rounded-md border-2 transition-all flex items-center justify-between text-left min-h-[52px] ${
        active
          ? "border-[#00C800] bg-[#00C800]/10"
          : "border-muted hover:border-foreground/50"
      }`}
    >
      <div className="font-medium text-[13px] md:text-sm text-left text-foreground">
        {label}
      </div>
      <div
        className={`ml-3 w-5 h-5 rounded-full grid place-items-center ${
          active
            ? "bg-[#00C800] text-white"
            : "bg-transparent text-transparent border border-muted"
        }`}
      >
        <Check className="w-3.5 h-3.5" />
      </div>
    </button>
  );
}
 
export default function InvestorRiskProfile() {
  const quiz = quizData as QuizSchema;

  const [started, setStarted] = useState(false);
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [showResults, setShowResults] = useState(false);
  const [showExplain, setShowExplain] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  const [madeFirstSelection, setMadeFirstSelection] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [dir, setDir] = useState<"fwd" | "back">("fwd");
  const autoTimerRef = useRef<number | null>(null);
  const navigate = useNavigate();

  // Email gate state
  const [gateOpen, setGateOpen] = useState(false);
  const [gateEmail, setGateEmail] = useState("");
  const [gateError, setGateError] = useState<string | null>(null);
  const [gateSubmitting, setGateSubmitting] = useState(false);


  const resetQuiz = () => {
    try {
      sessionStorage.removeItem("alts__quiz_answers");
      sessionStorage.removeItem("alts__quiz_step");
      sessionStorage.removeItem("alts__quiz_started");
    } catch {
      // ignore
    }
    setAnswers({});
    setCurrentIndex(0);
    setShowResults(false);
    setStarted(true);
  };

  const visible = useMemo(
    () =>
      quiz.questions.filter((q) =>
        evaluateCondition(q.conditional_display, answers, quiz.questions),
      ),
    [quiz.questions, answers],
  );
  const totalSteps = visible.length;
  const step = Math.min(currentIndex + 1, totalSteps);
  const countsReady = Boolean((answers["q1_duration"]?.length || 0) > 0);

  const currentQuestion: QuizQuestion | undefined = visible[currentIndex];

  // Previously captured email/flag in this session
  const lastLeadEmail = useMemo(() => {
    try { return sessionStorage.getItem("alts__lead_email") || undefined; } catch { return undefined; }
  }, []);
  const lastCapturedFlag = useMemo(() => {
    try { return sessionStorage.getItem("alts__lead_captured") === "1"; } catch { return false; }
  }, []);

  useEffect(() => {
    if (lastLeadEmail) setGateEmail(lastLeadEmail);
  }, [lastLeadEmail]);

  const onSelect = (qid: string, val: number, multi?: boolean) => {
    setAnswers((prev) => {
      const prevArr = prev[qid] || [];
      const nextArr = multi
        ? prevArr.includes(val)
          ? prevArr.filter((v) => v !== val)
          : [...prevArr, val]
        : [val];
      return { ...prev, [qid]: nextArr };
    });

    if (!madeFirstSelection) setMadeFirstSelection(true);

    if (!multi) {
      if (autoTimerRef.current) window.clearTimeout(autoTimerRef.current);
      autoTimerRef.current = window.setTimeout(() => {
        const cq = visible[currentIndex];
        const ok =
          !!cq && ((answers[cq.id]?.length || 0) > 0 || qid === cq?.id);
        if (!ok) return;

        setDir("fwd");
        if (currentIndex + 1 < totalSteps) setCurrentIndex(currentIndex + 1);
        else {
          // Show results and gate if no email captured yet
          setShowResults(true);
          if (!lastCapturedFlag && !lastLeadEmail) {
            setGateOpen(true);
          }
        }
      }, 1200);
    }
  };

  const goNext = () => {
    setDir("fwd");
    const cq = visible[currentIndex];
    const ok = !!cq && (answers[cq.id]?.length || 0) > 0;
    if (!ok) return;

    if (currentIndex + 1 < totalSteps) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setShowResults(true);
      if (!lastCapturedFlag && !lastLeadEmail) {
        setGateOpen(true);
      }
    }
  };

  const goBack = () => {
    setDir("back");
    if (showResults) {
      setShowResults(false);
      return;
    }
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
    else setStarted(false);
  };

  const submitGate = async () => {
    if (gateSubmitting) return;
    setGateSubmitting(true);
    try {
      setGateError(null);
      const basic = validateEmailFormat(gateEmail);
      if (!basic.isValid) {
        setGateError(basic.error || "Please enter a valid email address");
        return;
      }
      const sugg = validateEmailWithSuggestion(gateEmail);
      if (sugg.suggestion && sugg.suggestion !== gateEmail.trim()) {
        setGateError(`Did you mean ${sugg.suggestion}?`);
        return;
      }
      const trimmed = gateEmail.trim();
      try {
        sessionStorage.setItem("alts__lead_email", trimmed);
        sessionStorage.setItem("alts__lead_captured", "1");
      } catch {}
      try {
        const params = new URLSearchParams(window.location.search);
        const u: Record<string, string> = {};
        const keys = [
          "utm_source",
          "utm_medium",
          "utm_campaign",
          "utm_term",
          "utm_content",
          "gclid",
          "fbclid",
          "msclkid",
        ];
        keys.forEach((k) => {
          const v = params.get(k);
          if (v) u[k] = v;
        });
        const meta: any = {
          riskiq: { score: displayScore, band: band.label },
          ts: new Date().toISOString(),
        };
        if (Object.keys(u).length) meta.utm = u;

        const { error: leadErr } = await (supabase as any)
          .from("user_leads")
          .insert([
            {
              email: trimmed,
              phone: null,
              source: "riskiq",
              metadata: meta,
            },
          ]);
        if (leadErr) {
          const msg = (leadErr as any)?.message || "";
          const code = (leadErr as any)?.code;
          // Swallow duplicates to mimic ON CONFLICT DO NOTHING
          if (code !== "23505" && !/duplicate key value/i.test(msg)) {
            throw leadErr;
          }
        }
      } catch {}
      setGateOpen(false);
    } finally {
      setGateSubmitting(false);
    }
  };

  // Persist / re-hydrate answers & started state
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("alts__quiz_answers");
      const rawIdx = sessionStorage.getItem("alts__quiz_step");
      const rawStarted = sessionStorage.getItem("alts__quiz_started");
      if (raw) setAnswers(JSON.parse(raw));
      if (rawIdx) setCurrentIndex(Number(rawIdx));
      if (rawStarted === "1") setStarted(true);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem("alts__quiz_answers", JSON.stringify(answers));
      sessionStorage.setItem("alts__quiz_step", String(currentIndex));
      sessionStorage.setItem("alts__quiz_started", started ? "1" : "0");
    } catch {
      // ignore
    }
  }, [answers, currentIndex, started]);

  // Compute scores + band
  const { tolerance, capacity, preference, finalScore: fs } = computeScores(
    answers as any,
    quiz,
  );

  useEffect(() => {
    setFinalScore(fs);
  }, [fs]);

  const band = getRiskBand(Math.max(5, Math.round(finalScore)));
  const postureLabel = useMemo(() => {
    const map: Record<string, string> = {
      Cautious: "CAUTIOUS",
      Conservative: "CONSERVATIVE",
      Moderate: "MODERATE",
      Growth: "GROWTH",
      Aggressive: "AGGRESSIVE",
    };
    return map[band.label] || "MODERATE";
  }, [band.label]);
  const riskAccent = "#FFDF20";

  // Display guardrails: floor the visual score to 5 to avoid showing 0
  const displayScore = Math.max(5, Math.round(finalScore));

  const inIntro = !started && !showResults;
  const inQuiz = started && !showResults;
  const inResults = started && showResults;
  const headerTextClass = inResults ? "text-slate-700 hover:text-slate-900" : "text-slate-200 hover:text-white";

  // Fix background shorthand warning & keep visual states separate
  const rootStyle =
    inIntro
      ? {
          backgroundImage:
            "radial-gradient(1200px 600px at 50% 100%, rgba(255,255,255,0.18), rgba(255,255,255,0) 62%), linear-gradient(180deg, #051424 0%, #0A1E33 100%)",
          backgroundColor: "#051424",
          backgroundAttachment: "fixed, scroll" as const,
        }
      : inQuiz
      ? {
          backgroundColor: "#0B2740",
        }
      : {
          backgroundColor: "#F2F4F7",
        };

  return (
    <div className="min-h-screen" style={rootStyle}>
      <SeoHead
        title="Investor Risk Profile"
        description="A short quiz to estimate your investing risk score."
        canonicalPath="/tools/investor-risk-profile"
      />
      <main>
        {/* GLOBAL HEADER: INTRO (brand only) + QUIZ/RESULTS (back + brand) */}
        <header className="pt-4 md:pt-6">
          <PageContainer
            center={false}
            maxWidthClassName="max-w-[1280px]"
            className="px-6 md:px-8"
          >
            {inIntro ? (
              <div
                className="inline-flex items-baseline text-white/90 font-semibold text-[21px] md:text-[23px] tracking-[-0.01em]"
                style={{
                  fontFamily:
                    'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                }}
              >
                <span>Unis Lumen</span>
                <span style={{ color: "#FF9500" }}>.</span>
              </div>
            ) : (
              <button
                onClick={goBack}
                className={`inline-flex items-center gap-2 text-sm md:text-base ${headerTextClass}`}
              >
                <ArrowLeft className="w-5 h-5 md:w-6 md:h-6" />
                <span
                  className="inline-flex items-baseline font-semibold tracking-[-0.01em]"
                  style={{
                    fontFamily:
                      'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  }}
                >
                  <span>Unis Lumen</span>
                  <span style={{ color: "#FF9500" }}>.</span>
                </span>
              </button>
            )}
          </PageContainer>
        </header>

        <PageContainer
          center={false}
          maxWidthClassName="max-w-[1280px]"
          withVerticalPadding={false}
          className="px-6 md:px-8"
        >
          {/* INTRO HERO */}
          {inIntro && (
            <>
              <section className="relative py-10 md:py-16 lg:py-20">
                <div className="w-full mr-auto">
                  <div className="grid items-center gap-x-10 gap-y-10 md:grid-cols-2 md:min-h-[560px]">
                    {/* LEFT COPY COLUMN */}
                    <div className="self-center max-w-[560px]">
                      <div
                        className="text-[14px] font-medium tracking-[0.16em] text-white/70 mb-3 text-left uppercase"
                        style={{
                          fontFamily:
                            "'Instrument Sans', Inter, system-ui, sans-serif",
                        }}
                      >
                        RiskIQ Score™
                      </div>
                      <h1
                        className="text-[28px] md:text-[34px] lg:text-[44px] font-bold leading-[1.12] tracking-tight text-[#F1F5FB]"
                        style={{
                          fontFamily:
                            "'Instrument Sans', Inter, system-ui, sans-serif",
                        }}
                      >
                        Discover your real risk level{"\u00A0\u2013\u00A0"}
                        before you invest another dollar.
                      </h1>
                      <p
                        className="text-[15px] md:text-[16px] text-white/90 mt-5 mb-7 max-w-prose font-normal"
                        style={{
                          fontFamily:
                            "'Instrument Sans', Inter, system-ui, sans-serif",
                        }}
                      >
                        Takes less than a minute.
                      </p>
                      <div className="mt-0 flex md:block justify-start">
                        <Button
                          className="h-12 md:h-12 rounded-[6px] px-7 text-[16px] md:text-[18px] font-semibold tracking-[0.01em] font-barlow w-full md:w-auto"
                          style={{
                            backgroundColor: "#009B3E",
                            borderColor: "#009B3E",
                            color: "#FFFFFF",
                            filter: "saturate(0.92)",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#00B34A";
                            e.currentTarget.style.borderColor = "#00B34A";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "#009B3E";
                            e.currentTarget.style.borderColor = "#009B3E";
                          }}
                          onClick={() => {
                            setStarted(true);
                            setCurrentIndex(0);
                          }}
                        >
                          Create My Risk Profile
                        </Button>
                      </div>
                    </div>

                    {/* RIGHT DEVICE IMAGE */}
                    <div className="relative flex justify-center md:justify-end lg:-translate-y-10 lg:-translate-x-8">
                      <div
                        aria-hidden
                        className="absolute -z-10 right-0 bottom-0 w-[260px] h-[260px] md:w-[260px] md:h-[260px]"
                        style={{
                          background:
                            "radial-gradient(260px 260px at 58% 12%, rgba(255,255,255,0.30), rgba(0,0,0,0) 60%)",
                          filter: "blur(16px)",
                        }}
                      />
                      <img
                        src="/images/riskiq-device.png"
                        alt="Devices showing your RiskIQ score and an allocation example"
                        className="w-full max-w-[420px] md:max-w-[480px] lg:max-w-[640px] xl:max-w-[720px] h-auto drop-shadow-2xl md:drop-shadow-[0_30px_70px_rgba(0,0,0,0.45)]"
                      />
                    </div>
                  </div>
                </div>
              </section>

              {/* Intro disclaimer strip */}
              <section className="mt-24 md:mt-32 border-t border-slate-500/40 py-10 md:py-14">
                <div className="max-w-4xl mx-auto">
                  <DisclaimerFull tone="dark" />
                </div>
              </section>
            </>
          )}

          {/* QUIZ CARD */}
          {inQuiz && (
            <section className="pt-4 pb-10 md:pt-6 md:pb-24">
              <div className="max-w-2xl mx-auto">
                <div className="relative rounded-md border bg-card shadow-xl overflow-hidden">
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                      key={currentIndex}
                      className="p-6"
                      style={{ willChange: "transform" }}
                      initial={{ x: dir === "fwd" ? "100%" : "-100%" }}
                      animate={{
                        x: 0,
                        transition: {
                          duration: 0.3,
                          ease: [0.22, 1, 0.36, 1],
                        },
                      }}
                      exit={{
                        opacity: 0,
                        transition: { duration: 0.08, ease: "linear" },
                      }}
                    >
                      <div>
                        <div className="-m-6 mb-6 rounded-t-md bg-slate-50/70 border-b border-slate-200/70 p-6">
                          <div className="flex items-start justify-between gap-3">
                            <div className="w-2/3 md:w-3/4">
                              <div className="text-[13px] font-medium text-muted-foreground mb-2 font-barlow">
                                {currentIndex === 0
                                  ? "Question 1"
                                  : countsReady
                                  ? `Question ${step}/${totalSteps}`
                                  : "Question"}
                              </div>
                              <Progress
                                className="h-1.5 bg-slate-200/70"
                                value={
                                  countsReady
                                    ? Math.round((step / totalSteps) * 100)
                                    : 0
                                }
                                indicatorClassName="bg-[#00C800]"
                              />
                            </div>
                            {(currentQuestion?.why ||
                              currentQuestion?.meaning) && (
                              <button
                                className="text-[13px] underline text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 font-barlow"
                                onClick={() => setShowInfo(true)}
                              >
                                Why we ask
                                <MessageCircleQuestion className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                        {currentQuestion?.connector && (
                          <p className="text-[13px] text-muted-foreground mb-3">
                            {currentQuestion.connector}
                          </p>
                        )}
                        <h2 className="text-[18px] md:text-[22px] font-semibold mb-3">
                          {currentQuestion?.question}
                        </h2>
                        <div
                          className={`grid grid-cols-1 ${(() => {
                            const q = currentQuestion;
                            if (!q) return "md:grid-cols-1";
                            const maxLen = Math.max(
                              ...q.options.map((o) => o.label.length),
                            );
                            const single = maxLen > 42;
                            return single ? "md:grid-cols-1" : "md:grid-cols-2";
                          })()} gap-2.5 mb-6`}
                        >
                          {currentQuestion &&
                            currentQuestion.options.map((o, i) => {
                              const active = (
                                answers[currentQuestion.id] || []
                              ).includes(o.value);
                              return (
                                <div key={i}>
                                  <Choice
                                    active={active}
                                    label={o.label}
                                    onClick={() =>
                                      onSelect(
                                        currentQuestion.id,
                                        o.value,
                                        currentQuestion.multi_select,
                                      )
                                    }
                                  />
                                </div>
                              );
                            })}
                          {currentQuestion?.multi_select && (
                            <div className="md:justify-self-end">
                              <Button
                                size="sm"
                                className="gap-2 text-[13px] font-medium tracking-[0.01em] font-barlow"
                                onClick={goNext}
                                disabled={
                                  !(
                                    (answers[currentQuestion.id] || [])
                                      .length
                                  )
                                }
                              >
                                Continue <ArrowRight className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
            </section>
          )}

          {/* RESULTS PANEL */}
          {inResults && (
            <>
              <section className="pt-6 pb-10 md:pt-8">
                <div className="max-w-4xl mx-auto">
                  <div className="relative rounded-md border border-transparent bg-[#0B2740] shadow-xl overflow-hidden">
                  <div className="grid md:grid-cols-[minmax(360px,440px)_auto_minmax(360px,440px)] items-start md:gap-8 md:justify-items-center">
                    <div className="px-6 pt-10 pb-6 flex flex-col items-center">
                      <h3 className="text-lg md:text-xl font-semibold mb-5 text-[#F1F5FB]">
                        Your RiskIQ Score
                        <sup className="text-[8px] align-super ml-0.5">TM</sup>
                      </h3>
                      {gateOpen ? (
                        <div className="w-[320px] h-[200px] flex items-center justify-center select-none">
                          <div className="text-5xl md:text-6xl font-extrabold text-[#C9D3E1]">??</div>
                        </div>
                      ) : (
                        <RiskIQGaugeHC
                          value={displayScore}
                          size={320}
                          leftLabel="Very Low"
                          rightLabel="Very High"
                          onExplain={() => setShowExplain(true)}
                        />
                      )}
                      <div className="-mt-8 flex flex-col items-center">
                        <div className="text-sm font-medium mb-1 text-[#C9D3E1]">
                          Your risk posture
                        </div>
                        <h2
                          className="font-barlow font-extrabold uppercase leading-tight text-3xl md:text-4xl select-none"
                          style={{ color: riskAccent }}
                        >
                          {gateOpen ? "—" : postureLabel}
                        </h2>
                        <div className="mt-3 text-xs">
                          <button
                            className="underline text-[#C9D3E1] hover:text-white"
                            style={{ textUnderlineOffset: 4 }}
                            onClick={resetQuiz}
                          >
                            Click to retake assessment.
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="hidden md:block w-px bg-slate-200 self-start h-[320px] mt-[64px]" />
                    <div className="p-6 flex flex-col items-center md:items-start justify-center w-full md:self-center md:justify-self-center mx-auto md:min-h-[320px] max-w-[400px] pt-0 md:mt-4">
                      <h3 className="text-lg font-semibold mb-3 text-[#F1F5FB] text-center md:text-left">
                        Does your portfolio actually match this?
                      </h3>
                      <p className="text-[13px] text-[#C9D3E1] mb-5 text-center md:text-left select-none">
                        You scored <strong style={{ color: riskAccent }}>{gateOpen ? "?? (—)" : `${displayScore} (${band.label})`}</strong>.
                      </p>
                      <p className="text-[13px] text-[#C9D3E1] mb-4 text-center md:text-left flex items-start gap-3">
                        <MessageCircleQuestion className="w-8 h-8 mt-[2px] shrink-0 text-[#C9D3E1]" aria-hidden="true" />
                        <span>Did you know 68% of Canadian investors have portfolios that don't match their risk capacity?</span>
                      </p>
                      <p className="text-[13px] text-[#E6EDF5] mt-2 mb-7 text-center md:text-left font-medium">Find out where you stand.</p>
                      <Button
                        className="w-[260px] md:w-[272px] h-12 md:h-12 rounded-full text-[16px] md:text-[18px] font-bold tracking-[0.01em] font-barlow mx-auto"
                        style={{
                          backgroundColor: "#009B3E",
                          borderColor: "#009B3E",
                          color: "#FFFFFF",
                          filter: "saturate(0.92)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#00B34A";
                          e.currentTarget.style.borderColor = "#00B34A";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#009B3E";
                          e.currentTarget.style.borderColor = "#009B3E";
                        }}
                        onClick={() => {
                          let captured = "";
                          try { captured = sessionStorage.getItem("alts__lead_email") || ""; } catch {}
                          const emailParam = (captured || gateEmail || "").trim();
                          const url = `/auth?mode=signup&source=riskiq&score=${displayScore}&band=${encodeURIComponent(band.label)}${emailParam ? `&email=${encodeURIComponent(emailParam)}` : ""}`;
                          navigate(url);
                        }}
                      >
                        Get My Portfolio Analysis →
                      </Button>
                      <div className="w-full mt-4 text-[13px] text-[#C9D3E1] text-center space-y-1 connection-options">
                        <p>Access 150+ Canadian brokerages</p>
                        <p>Bank-level security</p>
                        <p>✨ AI-import holdings</p>
                      </div>
                    </div>
                  </div>
                </div>
                </div>
              </section>

              {/* FAQ + disclaimer under results */}
              <section className="py-8">
                <div className="max-w-4xl mx-auto">
                  <h3 className="text-xl font-semibold text-[#0B2740] text-center mb-5">
                    Frequently Asked Questions
                  </h3>
                  <AccordionPrimitive.Root
                    type="single"
                    collapsible
                    defaultValue="risk"
                  >
                    <AccordionPrimitive.Item
                      value="risk"
                      className="mb-3 rounded-md border border-slate-300/70 bg-white"
                    >
                      <AccordionPrimitive.Header>
                        <AccordionPrimitive.Trigger className="group w-full flex items-center justify-between px-5 py-4 text-left font-semibold text-[#0B2740]">
                          <span>What is the RiskIQ Score?</span>
                          <span className="ml-4 grid place-items-center">
                            <Plus className="h-4 w-4 text-slate-600 group-data-[state=open]:hidden" />
                            <Minus className="h-4 w-4 text-slate-600 hidden group-data-[state=open]:block" />
                          </span>
                        </AccordionPrimitive.Trigger>
                      </AccordionPrimitive.Header>
                      <AccordionPrimitive.Content className="px-5 pb-4 text-[14px] leading-6 text-slate-700 font-barlow">
                        <p className="mb-3">
                          Your RiskIQ Score reflects both how you feel about
                          risk (tolerance) and how much risk your financial
                          profile can support (capacity). A higher score
                          generally points to a growth-oriented mix; a lower
                          score leans toward stability.
                        </p>
                        <p>
                          When you connect your accounts, we generate your
                          Verified RiskIQ — a refined score based on your actual
                          assets and holdings, showing how your current
                          portfolio compares to the level of risk that truly
                          fits you.
                        </p>
                      </AccordionPrimitive.Content>
                    </AccordionPrimitive.Item>
                    <AccordionPrimitive.Item
                      value="allocation"
                      className="mb-3 rounded-md border border-slate-300/70 bg-white"
                    >
                      <AccordionPrimitive.Header>
                        <AccordionPrimitive.Trigger className="group w-full flex items-center justify-between px-5 py-4 text-left font-semibold text-[#0B2740]">
                          <span>What is asset allocation?</span>
                          <span className="ml-4 grid place-items-center">
                            <Plus className="h-4 w-4 text-slate-600 group-data-[state=open]:hidden" />
                            <Minus className="h-4 w-4 text-slate-600 hidden group-data-[state=open]:block" />
                          </span>
                        </AccordionPrimitive.Trigger>
                      </AccordionPrimitive.Header>
                      <AccordionPrimitive.Content className="px-5 pb-4 text-[14px] leading-6 text-slate-700 font-barlow">
                        <p>
                          Asset allocation is how your portfolio is divided
                          across the four core pillars of modern investing —
                          Equities, Fixed Income, Alternatives, and Cash.
                          Balancing these pillars helps manage risk and keep
                          your portfolio aligned with your goals and time
                          horizon.{" "}
                          <Link
                            to="/content/can/modern-wealth-playbook#concepts"
                            className="underline font-medium"
                          >
                            Learn more about allocation.
                          </Link>
                        </p>
                      </AccordionPrimitive.Content>
                    </AccordionPrimitive.Item>
                    <AccordionPrimitive.Item
                      value="safety"
                      className="mb-3 rounded-md border border-slate-300/70 bg-white"
                    >
                      <AccordionPrimitive.Header>
                        <AccordionPrimitive.Trigger className="group w-full flex items-center justify-between px-5 py-4 text-left font-semibold text-[#0B2740]">
                          <span>Is it safe to connect my investment accounts?</span>
                          <span className="ml-4 grid place-items-center">
                            <Plus className="h-4 w-4 text-slate-600 group-data-[state=open]:hidden" />
                            <Minus className="h-4 w-4 text-slate-600 hidden group-data-[state=open]:block" />
                          </span>
                        </AccordionPrimitive.Trigger>
                      </AccordionPrimitive.Header>
                      <AccordionPrimitive.Content className="px-5 pb-4 text-[14px] leading-6 text-slate-700 font-barlow">
                        <p>
                          Yes. We use bank-level encryption and read-only access through Wealthica (trusted by 100,000+ Canadians). We can only view your holdings - we cannot move money, make trades, or access your login credentials. Your data is never sold or shared with third parties.
                        </p>
                      </AccordionPrimitive.Content>
                    </AccordionPrimitive.Item>
                    <AccordionPrimitive.Item
                      value="timing"
                      className="mb-3 rounded-md border border-slate-300/70 bg-white"
                    >
                      <AccordionPrimitive.Header>
                        <AccordionPrimitive.Trigger className="group w-full flex items-center justify-between px-5 py-4 text-left font-semibold text-[#0B2740]">
                          <span>How long does it take to analyze my portfolio?</span>
                          <span className="ml-4 grid place-items-center">
                            <Plus className="h-4 w-4 text-slate-600 group-data-[state=open]:hidden" />
                            <Minus className="h-4 w-4 text-slate-600 hidden group-data-[state=open]:block" />
                          </span>
                        </AccordionPrimitive.Trigger>
                      </AccordionPrimitive.Header>
                      <AccordionPrimitive.Content className="px-5 pb-4 text-[14px] leading-6 text-slate-700 font-barlow">
                        <p>
                          Connecting your accounts takes 2 minutes. Portfolio analysis is instant once connected. You'll immediately see how your actual portfolio compares to your RiskIQ Score and get personalized recommendations.
                        </p>
                      </AccordionPrimitive.Content>
                    </AccordionPrimitive.Item>
                    <AccordionPrimitive.Item
                      value="multi"
                      className="mb-3 rounded-md border border-slate-300/70 bg-white"
                    >
                      <AccordionPrimitive.Header>
                        <AccordionPrimitive.Trigger className="group w-full flex items-center justify-between px-5 py-4 text-left font-semibold text-[#0B2740]">
                          <span>Can I connect multiple investment accounts?</span>
                          <span className="ml-4 grid place-items-center">
                            <Plus className="h-4 w-4 text-slate-600 group-data-[state=open]:hidden" />
                            <Minus className="h-4 w-4 text-slate-600 hidden group-data-[state=open]:block" />
                          </span>
                        </AccordionPrimitive.Trigger>
                      </AccordionPrimitive.Header>
                      <AccordionPrimitive.Content className="px-5 pb-4 text-[14px] leading-6 text-slate-700 font-barlow">
                        <p>
                          Yes. Connect all your accounts - RRSPs, TFSAs, taxable accounts, crypto, and alternative investments. We'll aggregate everything into one holistic view so you can see your complete financial picture and identify allocation gaps across all holdings.
                        </p>
                      </AccordionPrimitive.Content>
                    </AccordionPrimitive.Item>
                    <AccordionPrimitive.Item
                      value="advisor"
                      className="mb-3 rounded-md border border-slate-300/70 bg-white"
                    >
                      <AccordionPrimitive.Header>
                        <AccordionPrimitive.Trigger className="group w-full flex items-center justify-between px-5 py-4 text-left font-semibold text-[#0B2740]">
                          <span>I have a financial advisor. Can I still use this?</span>
                          <span className="ml-4 grid place-items-center">
                            <Plus className="h-4 w-4 text-slate-600 group-data-[state=open]:hidden" />
                            <Minus className="h-4 w-4 text-slate-600 hidden group-data-[state=open]:block" />
                          </span>
                        </AccordionPrimitive.Trigger>
                      </AccordionPrimitive.Header>
                      <AccordionPrimitive.Content className="px-5 pb-4 text-[14px] leading-6 text-slate-700 font-barlow">
                        <p>
                          Absolutely. Many investors use WealthKit alongside their advisor to verify recommendations align with their risk capacity, track all accounts in one place (including those outside their advisor's view), and explore alternative investments their advisor may not offer. Your portfolio data remains private unless you choose to share it.
                        </p>
                      </AccordionPrimitive.Content>
                    </AccordionPrimitive.Item>
                    <AccordionPrimitive.Item
                      value="postconnect"
                      className="rounded-md border border-slate-300/70 bg-white"
                    >
                      <AccordionPrimitive.Header>
                        <AccordionPrimitive.Trigger className="group w-full flex items-center justify-between px-5 py-4 text-left font-semibold text-[#0B2740]">
                          <span>What will I see after connecting my accounts?</span>
                          <span className="ml-4 grid place-items-center">
                            <Plus className="h-4 w-4 text-slate-600 group-data-[state=open]:hidden" />
                            <Minus className="h-4 w-4 text-slate-600 hidden group-data-[state=open]:block" />
                          </span>
                        </AccordionPrimitive.Trigger>
                      </AccordionPrimitive.Header>
                      <AccordionPrimitive.Content className="px-5 pb-4 text-[14px] leading-6 text-slate-700 font-barlow">
                        <ul className="list-disc pl-5 space-y-2">
                          <li>Your actual portfolio risk score vs. your RiskIQ capacity</li>
                          <li>Asset allocation breakdown (stocks, bonds, cash, alternatives, crypto)</li>
                          <li>Concentration risk analysis (are you over-invested in any single stock/sector?)</li>
                          <li>Comparison to model portfolios for your risk profile</li>
                          <li>Personalized recommendations to optimize your allocation</li>
                        </ul>
                      </AccordionPrimitive.Content>
                    </AccordionPrimitive.Item>
                  </AccordionPrimitive.Root>
                  <DisclaimerFull tone="light" className="mt-8" />
                </div>
              </section>
            </>
          )}
        </PageContainer>
      </main>

      {/* FOOTER DISCLAIMER ON QUIZ STEPS */}
      {inQuiz && (
        <DisclaimerMini
          tone="dark"
          align="left"
          fixedOnDesktop={true}
          containerPadding="px-6 md:px-8"
          parentMaxWidthClassName="max-w-[1280px]"
          innerMaxWidthClassName="max-w-2xl"
          className="mt-6 pb-16 md:mt-0 md:pb-0"
          extraBottomPaddingPx={32}
        />
      )}

      {/* SCORE EXPLAIN MODAL */}
      <Dialog open={showExplain} onOpenChange={setShowExplain}>
        <DialogContent className="sm:max-w-xl bg-white">
          <DialogTitle className="font-semibold text-lg text-[#0B2740]">
            What is the RiskIQ Score?
          </DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-3 text-[14px] leading-6 text-slate-800 font-barlow">
              <p>
                Your RiskIQ Score reflects the level of variability your portfolio
                can reasonably tolerate and your comfort taking risk. It
                combines risk tolerance and risk capacity, with a small
                preference adjustment.
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <span className="font-semibold">Tolerance</span> – behavioural
                  comfort with ups and downs.
                </li>
                <li>
                  <span className="font-semibold">Capacity</span> – financial
                  ability to withstand variability (time horizon, liquidity,
                  withdrawals, etc.).
                </li>
                <li>
                  <span className="font-semibold">Preference</span> – your
                  stated lean toward growth vs stability.
                </li>
              </ul>
              <p>
                A higher score is not “better” and a lower score is not “worse”
                — it simply describes what may feel sustainable for you.
              </p>
            </div>
          </DialogDescription>
        </DialogContent>
      </Dialog>

      {/* QUESTION EXPLAIN MODAL */}
      <Dialog open={showInfo} onOpenChange={setShowInfo}>
        <DialogContent className="sm:max-w-xl bg-[#0B2740]">
          <DialogTitle className="text-white">About this question</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-6 text-[14px] leading-6 text-slate-200 font-barlow">
              {currentQuestion?.why && (
                <section>
                  <h4 className="font-semibold mb-2 text-white">Why we ask</h4>
                  <p className="whitespace-pre-line">{currentQuestion.why}</p>
                </section>
              )}
              {currentQuestion?.meaning && (
                <section>
                  <h4 className="font-semibold mb-2 text-white">
                    What your answer indicates
                  </h4>
                  <p className="whitespace-pre-line">
                    {currentQuestion.meaning}
                  </p>
                </section>
              )}
            </div>
          </DialogDescription>
        </DialogContent>
      </Dialog>

      {/* EMAIL GATE OVERLAY (mandatory, minimal) */}
      {showResults && gateOpen && (
        <div className="fixed inset-0 z-[60] bg-black/30 backdrop-blur-sm">
          <div className="absolute inset-0 flex items-center justify-center px-4">
            <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-2xl">
              <h3 className="text-lg font-semibold text-[#0B2740] mb-1">Enter your email to view your score</h3>
              <p className="text-sm text-slate-600 mb-4">We’ll save this and prefill signup. No spam.</p>
              <div className="space-y-3">
                <Input
                  type="email"
                  placeholder="enter your email"
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  className="placeholder:text-slate-300/60"
                  value={gateEmail}
                  onChange={(e) => { setGateEmail(e.target.value); setGateError(null); }}
                  autoFocus
                />
                {gateError && (
                  <div className="text-sm text-red-600">{gateError}</div>
                )}
                <Button
                  className="w-full bg-[#009B3E] hover:bg-[#00B34A]"
                  disabled={gateSubmitting}
                  onClick={submitGate}
                >
                  {gateSubmitting ? "Saving..." : "Continue →"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}
